import React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { VersionStore, ExerciseStore, CampaignStore } from '@seedlang/stores';
import { computed } from 'mobx';
import autobind from 'autobind-decorator';
import { AppUI } from '@seedlang/state';
import Spinner from 'components/spinner';
import { isBlank, isPresent } from '@seedlang/utils';
import { Link } from 'react-router';

const Wrapper = styled.div`
    margin-top: ${props => props.marginTop};
    margin-bottom: ${props => props.marginBottom};
    padding: 20px;
    background: #FFF;
    border-radius: ${props => props.borderRadius};
    h1, h2, h3 {
      line-height: 32px;
    }
    p {
      margin: 10px 0!important;
    }
    .version-details {
      margin-top: 20px;
    }
    .version-details-row {
      display: flex;
      margin-bottom: 10px;
    }
    .version-details-label {
      font-weight: bold;
      width: 150px;
    }
    .version-details-value {
      flex: 1;
    }
    .admin-actions {
      margin-top: 20px;
      display: flex;
      justify-content: flex-end;
    }
    table {
      width: fit-content !important;
    }
    td:last-child  {
      padding-right: 10px !important;
    }
`;

@observer
class VersionShow extends React.Component {
  componentDidMount() {
    this.getVersion();
    this.getExerciseData();
    this.getCampaignData();
  }

  componentDidUpdate(prevProps) {
    if (this.props.routeParams.versionId !== prevProps.routeParams.versionId) {
      this.getVersion();
    }
    this.getExerciseData();
    this.getCampaignData();
  }

  componentWillUnmount() {
    VersionStore.clearShowData();
  }

  @computed get version() {
    return VersionStore.showData;
  }

  @autobind getVersion() {
    VersionStore.getShow({ ids: { versionId: this.props.routeParams.versionId }});
  }

  @autobind getExerciseData() {
    if (this.version?.itemType === 'Exercise' && !ExerciseStore.hasShowData) {
      ExerciseStore.getShow({ ids: { exerciseId: this.version.itemId } });
    }
  }

  @autobind getCampaignData() {
    if (this.version?.itemType === 'Campaign' && !CampaignStore.hasShowData) {
      CampaignStore.getShow({ ids: { campaignId: this.version.itemId } });
    }
  }

  @computed get worksheetId() {
    if (this.version?.itemType === 'Exercise' && ExerciseStore.hasShowData && ExerciseStore.showData.id === this.version.itemId) {
      return ExerciseStore.showData.worksheetId;
    }
  }

  @computed get campaignTypeId() {
    if (this.version?.itemType === 'Campaign' && CampaignStore.hasShowData && CampaignStore.showData.id === this.version.itemId) {
      return CampaignStore.showData.campaignTypeId;
    }
  }

  displayPropertyInTable(value) {
    if (isBlank(value)) {
      return '<empty>';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return value.toString();
  }

  render() {
    return (
      <Wrapper
        marginTop={AppUI.layout.isMobile ? '15px' : null}
        marginBottom={AppUI.layout.isMobile ? '60px' : null}
        borderRadius={AppUI.layout.isMobile ? '0' : '10px'}
      >
        {
          VersionStore.requestCounter > 0 &&
          <Spinner background={AppUI.site.accentColor} />
        }
        {
          isPresent(this.version) &&
            <>
              <h2>Admin Action Details</h2>

              <div className='version-details'>
                <div className='version-details-row'>
                  <div className='version-details-label'>Item:</div>
                  <div className='version-details-value'>
                    {this.version.itemType} {this.version.itemId}
                  </div>
                </div>

                <div className='version-details-row'>
                  <div className='version-details-label'>Event:</div>
                  <div className='version-details-value' style={{ textTransform: 'capitalize' }}>{this.version.event}</div>
                </div>

                <div className='version-details-row'>
                  <div className='version-details-label'>Modified At:</div>
                  <div className='version-details-value'>{this.version.createdAt.formattedDateWithYearAndTime}</div>
                </div>

                <div className='version-details-row'>
                  <div className='version-details-label'>Modified By:</div>
                  <div className='version-details-value'>
                    {isPresent(this.version.user) ? (
                      <Link
                        to={{
                          name: 'builder.users.edit',
                          params: { userId: this.version.user.id },
                        }}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='underline'
                      >
                        {this.version.user.name}
                      </Link>
                    ) : (
                      'Unknown User'
                    )}
                  </div>
                </div>
              </div>

              {this.version.changes && (
                <>
                  <h3>Changes</h3>
                  <table className='table-wrapper admin-table'>
                    <thead>
                      <tr>
                        <th>Property</th>
                        <th>Before</th>
                        <th>After</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(this.version.changesJson).map(([property, [before, after]]) => (
                        <tr key={property}>
                          <td>{property}</td>
                          <td>{this.displayPropertyInTable(before)}</td>
                          <td>{this.displayPropertyInTable(after)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

              <div className='admin-actions'>
                <Link className='gray-button' to='builder.versions.index'>
                  Back to Admin Logs
                </Link>
              </div>
            </>
        }
      </Wrapper>
    );
  }
}

export default VersionShow;